import type { NavigationScreenState } from '@gitbook/app-navigation';

import type { NavigateFunction } from '../../navigation';
import { prepareSiteCentricWelcomeDialog } from './SiteCentricWelcomeDialogAssets';

const SITE_CENTRIC_TRANSITION_MINIMUM_DURATION_MS = 1500;

interface SiteCentricTransition {
    startedAt: number;
}

/**
 * Start tracking the site-centric transition duration.
 */
function startSiteCentricTransition(): SiteCentricTransition {
    return {
        startedAt: Date.now(),
    };
}

/**
 * Wait until the site-centric transition overlay has had a chance to paint.
 */
async function waitForSiteCentricTransitionOverlay(timeoutMs = 250): Promise<void> {
    await new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
            if (done) {
                return;
            }

            done = true;
            resolve();
        };
        const timeout = window.setTimeout(finish, timeoutMs);

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                window.clearTimeout(timeout);
                finish();
            });
        });
    });
}

/**
 * Wait until the site-centric transition has been visible long enough to be readable.
 */
async function endSiteCentricTransition(transition: SiteCentricTransition) {
    const remainingDuration =
        SITE_CENTRIC_TRANSITION_MINIMUM_DURATION_MS - (Date.now() - transition.startedAt);

    if (remainingDuration <= 0) {
        return;
    }

    await new Promise<void>((resolve) => {
        window.setTimeout(resolve, remainingDuration);
    });
}

/**
 * Apply the site-centric navigation transition.
 */
interface TransitionSiteCentricParams {
    organizationId: string;
    landingScreen?: NavigationScreenState;
    reloadingMessage: string;
    siteCentricOptIn: 'true' | 'false';
    navigate: NavigateFunction;
    onUpdated?: () => void | Promise<void>;
    setAppReloadingMessage: (message: string | null) => void;
    setSiteCentricOptIn?: (value: 'true' | 'false' | null) => void;
    updateUser: (opts: { [key: string]: string | number | undefined }) => Promise<void>;
}
export async function transitionSiteCentric(params: TransitionSiteCentricParams) {
    const {
        organizationId,
        landingScreen,
        navigate,
        siteCentricOptIn,
        setAppReloadingMessage,
        setSiteCentricOptIn,
        reloadingMessage,
        updateUser,
        onUpdated,
    } = params;
    const transition = startSiteCentricTransition();

    setAppReloadingMessage(reloadingMessage);

    try {
        // Make the loading screen appear before Reflag updates the flag and the app reacts to the new value.
        await waitForSiteCentricTransitionOverlay();
        setSiteCentricOptIn?.(siteCentricOptIn);

        try {
            await updateUser({ siteCentricOptIn });
        } catch (error) {
            setSiteCentricOptIn?.(null);
            throw error;
        }

        if (onUpdated) {
            try {
                await onUpdated();
            } catch {
                // Non-blocking hook; avoid breaking the transition flow.
            }
        }

        if (siteCentricOptIn === 'true') {
            await prepareSiteCentricWelcomeDialog();
        }

        navigate(
            {
                overlay: null,
                screen: landingScreen ?? {
                    type: 'home',
                    organization: organizationId,
                },
            },
            { replace: true, suspenseTransition: true }
        );
        // Give React a chance to commit the home screen behind the loading screen before we reveal it.
        await waitForSiteCentricTransitionOverlay();
        await endSiteCentricTransition(transition);
    } finally {
        setAppReloadingMessage(null);
    }
}
